import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Scheduling|Accommodation|Create|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.ExamAccommodations,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateSchedulingAccommodationCreate';
export type SPCandidateSchedulingAccommodationCreateLocNames =
	| `${LocNameStart}TitleTab`
	| `${LocNameStart}TitlePage`
	| `${LocNameStart}NotificationAccomCreated`
	| `${LocNameStart}NotificationFileUploadError`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonRequest`
	| `${LocNameStart}ContentNoAccommodations`
	| `${LocNameStart}LabelAccommodation`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}LabelDescription`
	| `${LocNameStart}LabelNotes`
	| `${LocNameStart}LabelDocumentationFile`
	| `${LocNameStart}ContentFileHint`
	| `${LocNameStart}ErrorSelectionRequired`
	| `${LocNameStart}ErrorExpireDateMin`
	| `${LocNameStart}ErrorFileSizeError`
	| `${LocNameStart}ErrorFileTypeError`;

export const spCandidateSchedulingAccommodationCreateLocs: LocDefs<SPCandidateSchedulingAccommodationCreateLocNames> =
	{
		SPCandidateSchedulingAccommodationCreateTitleTab: {
			defaultValue: 'Request Accommodation',
			description: 'Tab title for the create accommodation page',
			displayPath: `${pathStart}Tab title`,
			displayType: 'Title',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Title.Createtab',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateTitlePage: {
			defaultValue: 'Request Accommodation',
			description: 'Page title for the create accommodation page',
			displayPath: `${pathStart}Page title`,
			displayType: 'Title',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Title.Createtab',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateNotificationAccomCreated: {
			defaultValue: 'Accommodation was successfully requested',
			description: 'Success notification message for creating an accommodation request',
			displayPath: `${pathStart}Accommodation Created`,
			displayType: 'Notification',
			legacyName:
				'Exam.Accommodations/Candidate.Accommodations.NotificationMessage.AccomCreated',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateNotificationFileUploadError: {
			defaultValue:
				'Accommodation was created successfully, however, there was an error while uploading the documentation file',
			description:
				'Error notification message for creating an accommodation request with a file upload error',
			displayPath: `${pathStart}File Upload Error`,
			displayType: 'Notification',
			legacyName:
				'Exam.Accommodations/Candidate.Accommodations.NotificationMessage.FileUploadError',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Cancel button text for the create accommodation page',
			displayPath: `${pathStart}Cancel`,
			displayType: 'Button',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Button.CancelRequest',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateButtonRequest: {
			defaultValue: 'Request',
			description: 'Request button text for the create accommodation page',
			displayPath: `${pathStart}Request`,
			displayType: 'Button',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Button.Request',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateContentNoAccommodations: {
			defaultValue: 'No available accommodations',
			description: 'Text displayed when there are no available accommodations to request',
			displayPath: `${pathStart}No available accommodations`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.Content.NoAccommodations',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateLabelAccommodation: {
			defaultValue: 'Accommodation',
			description: 'Label for: Accommodation.',
			displayPath: `${pathStart}Accommodation`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.Accommodation',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateLabelExpireDate: {
			defaultValue: 'Expire Date',
			description: 'Label for: Expire Date.',
			displayPath: `${pathStart}Expire Date`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.ExpireDate',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateLabelDescription: {
			defaultValue: 'Description',
			description: 'Label for: Description.',
			displayPath: `${pathStart}Description`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.Description',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateLabelNotes: {
			defaultValue: 'Notes',
			description: 'Label for: Notes.',
			displayPath: `${pathStart}Notes`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.Notes',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateLabelDocumentationFile: {
			defaultValue: 'Documentation File',
			description: 'Label for: Documentation File.',
			displayPath: `${pathStart}Documentation File`,
			displayType: 'Label',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldLabel.DoumentationFile',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateContentFileHint: {
			defaultValue: 'The file must be a ZIP format and not exceed 20mb',
			description: 'Hint for: Documentation File.',
			displayPath: `${pathStart}Documentation File Hint`,
			displayType: 'Content',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldHint.FileHint',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateErrorSelectionRequired: {
			defaultValue: 'You must select an accommodation',
			description: 'Accommodation selection required error message',
			displayPath: `${pathStart}Accommodation selection required`,
			displayType: 'Error',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldError.SelectionRequired',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateErrorExpireDateMin: {
			defaultValue: 'Expire dates must be in the future',
			description: 'Expire date min error message',
			displayPath: `${pathStart}Expire date min`,
			displayType: 'Error',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldError.ExpireDateMin',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateErrorFileSizeError: {
			defaultValue: 'The file must not exceed 20mb',
			description: 'File size error message',
			displayPath: `${pathStart}File Size Error`,
			displayType: 'Error',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldError.FileSizeError',
			...sharedProperties,
		},
		SPCandidateSchedulingAccommodationCreateErrorFileTypeError: {
			defaultValue: 'The file must be a ZIP format',
			description: 'File type error message',
			displayPath: `${pathStart}File Type Error`,
			displayType: 'Error',
			legacyName: 'Exam.Accommodations/Candidate.Accommodations.FieldError.FileTypeError',
			...sharedProperties,
		},
	};

export type SPCandidateSchedulingAccommodationCreateLocType =
	LocMap<SPCandidateSchedulingAccommodationCreateLocNames>;
